// Implements: F-PRV-009
/**
 * The in-app end of the frame's console relay (design 5.5, W3f.7).
 *
 * The frame runs in an opaque origin and posts `{channel, v, kind, level, text}` to its parent. The
 * parent trusts NONE of it: the frame's own caps are not a defence, because the model's script runs
 * in that frame and a hostile sibling frame can post to this window too. So this listener checks, in
 * order, `event.source` (the preview frame's own window; an opaque sibling also has origin
 * `"null"`, so the origin alone proves nothing), `event.origin === "null"`, the shape, a `kind` and
 * `level` allowlist, and then applies its own budget: at most 50 accepted messages per load plus a
 * separate 25 for `csp` reports (so a console flood cannot hide a violation), and `text` cut to 500
 * characters. Only the three validated fields are passed on, as text for a caller to render; the
 * rest of the payload never leaves this function. Anything else is ignored without a trace.
 */
import { useLayoutEffect, useRef } from "react";
import type { RefObject } from "react";

export const RELAY_MAX_MESSAGES = 50;
export const RELAY_MAX_CSP_MESSAGES = 25;
export const RELAY_MAX_TEXT = 500;

const CHANNEL = "prototyper:frame";
const KINDS: ReadonlySet<string> = new Set([
  "console",
  "error",
  "rejection",
  "csp",
  "normaliser",
  "disarm",
]);
const LEVELS: ReadonlySet<string> = new Set(["error", "warn"]);

export interface RelayedMessage {
  readonly kind: string;
  readonly level: string;
  readonly text: string;
}

/** At most `limit` code points, so a surrogate pair is never split; stops early on a huge text. */
function cutByCodePoint(text: string, limit: number): string {
  let out = "";
  let count = 0;
  for (const character of text) {
    if (count >= limit) break;
    out += character;
    count += 1;
  }
  return out;
}

function validate(data: unknown): RelayedMessage | null {
  if (typeof data !== "object" || data === null) return null;
  const record = data as Record<string, unknown>;
  const { channel, v, kind, level, text } = record;
  if (channel !== CHANNEL || v !== 1) return null;
  if (typeof kind !== "string" || !KINDS.has(kind)) return null;
  if (typeof level !== "string" || !LEVELS.has(level)) return null;
  if (typeof text !== "string") return null;
  return { kind, level, text: cutByCodePoint(text, RELAY_MAX_TEXT) };
}

/**
 * `resetKey` names one load of the frame: a new value starts the budgets again, because a reload
 * or another version is another document with its own allowance.
 */
export function useFrameRelay(
  frame: RefObject<HTMLIFrameElement | null>,
  resetKey: string,
  onMessage: (message: RelayedMessage) => void,
): void {
  // The latest callback, read at call time: a caller that passes a fresh closure on every render
  // must not restart the budgets, which belong to one load of the frame.
  const handler = useRef(onMessage);
  // A layout effect declared before the subscription: when a reload changes both the key and the
  // callback in one render, the first message must already find the new callback.
  useLayoutEffect(() => {
    handler.current = onMessage;
  }, [onMessage]);

  // A layout effect, so the listener exists before the frame can post: a cached document may send
  // its first csp or normaliser report as soon as it is attached, before any passive effect runs.
  useLayoutEffect(() => {
    let accepted = 0;
    let acceptedCsp = 0;
    const listener = (event: MessageEvent): void => {
      const own = frame.current?.contentWindow ?? null;
      if (own === null || event.source !== own) return;
      if (event.origin !== "null") return;
      const message = validate(event.data);
      if (message === null) return;
      if (message.kind === "csp") {
        if (acceptedCsp >= RELAY_MAX_CSP_MESSAGES) return;
        acceptedCsp += 1;
      } else {
        if (accepted >= RELAY_MAX_MESSAGES) return;
        accepted += 1;
      }
      handler.current(message);
    };
    window.addEventListener("message", listener);
    return () => {
      window.removeEventListener("message", listener);
    };
  }, [frame, resetKey]);
}
